import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { WindowService } from 'core/services/window.service';

@Directive({
  selector: '[buttonTap]',
  standalone: true,
})
export class ButtonTapDirective implements OnInit {
  @Input() highlightInset: number = 0; // Extend highlight beyond button bounds
  @Input() highlightInsetX: number = 0; // Extend highlight beyond button bounds
  @Input() highlightInsetY: number = 0; // Extend highlight beyond button bounds
  @Input() highlightDuration: number = 250; // Time in ms before highlight disappears
  @Input() highlightStyle?: 'secondary' | 'primary' | 'grey' | 'dark' | 'light' | 'warn' | 'text-grey' = 'light';

  private activeHighlight: HTMLElement | null = null; // Track active highlight
  private isAnimating = false; // Prevent rapid taps

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private window: WindowService,
  ) {}

  ngOnInit(): void {
    if (this.highlightStyle === 'text-grey') {
      this.el.nativeElement.style.transition = 'color .3s';
    }
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    if (event.touches.length > 1 || this.isAnimating) return;

    const button = this.el.nativeElement;

    if (
      button.hasAttribute('disabled') ||
      button.classList.contains('active') ||
      button.classList.contains('disabled') ||
      this.isAnimating
    ) {
      return;
    }

    if (this.highlightStyle === 'text-grey') {
      this.isAnimating = true;
      this.renderer.addClass(button, 'touch-color');
      this.renderer.addClass(button, this.highlightStyle);
      setTimeout(() => {
        this.renderer.removeClass(button, 'touch-color');
        this.renderer.removeClass(button, this.highlightStyle as 'text-grey');
        this.isAnimating = false; // Allow next tap
      }, this.highlightDuration);
      return;
    }

    this.isAnimating = true;
    const rect = this.window.getBoundingClientRect(button);
    this.renderer.setStyle(button, 'position', 'relative');

    // Remove existing highlight before adding a new one
    if (this.activeHighlight) {
      this.renderer.removeChild(button, this.activeHighlight);
      this.activeHighlight = null;
    }

    // Compute highlight size
    const highlightWidth = rect.width + this.highlightInset + this.highlightInsetX;
    const highlightHeight = rect.height + this.highlightInset + this.highlightInsetY;

    // Create highlight element
    const highlight = this.renderer.createElement('span') as HTMLElement;
    const radius = this.window.getComputedStyle(button)?.borderRadius;
    this.renderer.addClass(highlight, 'touch-highlight');
    this.renderer.addClass(highlight, this.highlightStyle as string);

    // Set highlight size and position (centered inside button)
    this.renderer.setStyle(highlight, 'width', `${highlightWidth}px`);
    this.renderer.setStyle(highlight, 'height', `${highlightHeight}px`);
    this.renderer.setStyle(highlight, 'left', `50%`);
    this.renderer.setStyle(highlight, 'top', `50%`);
    this.renderer.setStyle(highlight, 'transform', 'translate(-50%, -50%)');

    if (parseInt(radius || '0')) {
      this.renderer.setStyle(highlight, 'border-radius', `${radius}`);
    }

    // Store reference and prepend highlight to button
    this.activeHighlight = highlight;
    this.renderer.insertBefore(button, highlight, button.firstChild);

    // Remove highlight after delay
    setTimeout(() => {
      if (this.activeHighlight) {
        this.renderer.removeChild(button, this.activeHighlight);
        this.activeHighlight = null;
      }
      this.renderer.removeStyle(button, 'position');

      this.isAnimating = false; // Allow next tap
    }, this.highlightDuration);
  }
}
